import podcastPersonas from "@/data/podcastPersonas.json";
import type { PodcastSpeakerRole, PodcastTranscriptSegment } from "@/models/podcast";

export type PodcastPersonaGender = "female" | "male";

export interface PodcastPersona {
  id: string;
  name: string;
  gender: PodcastPersonaGender;
  ttsProvider: string;
  ttsVoice: string;
}

const PERSONAS = podcastPersonas as Record<
  string,
  Partial<Record<PodcastSpeakerRole, PodcastPersona>>
>;

function normalizePersonaLanguageCode(languageCode?: string) {
  const normalized = String(languageCode || "")
    .trim()
    .toLowerCase();
  if (normalized === "fr") return "fr-FR";
  if (normalized === "es") return "es-ES";
  if (normalized === "de") return "de-DE";
  if (normalized === "nl") return "nl-NL";
  return Object.keys(PERSONAS).find((code) => code.toLowerCase() === normalized) || "";
}

export function getPodcastPersona(languageCode: string | undefined, speaker: PodcastSpeakerRole) {
  const resolvedLanguage = normalizePersonaLanguageCode(languageCode);
  return PERSONAS[resolvedLanguage]?.[speaker] || null;
}

export function getPodcastPersonaPrompt(languageCode?: string) {
  const resolvedLanguage = normalizePersonaLanguageCode(languageCode);
  const cast = PERSONAS[resolvedLanguage];
  if (!cast) return "";

  const roles: PodcastSpeakerRole[] = [
    "Teacher",
    "Student",
    "Native Speaker 1",
    "Native Speaker 2",
  ];
  const lines = roles.map((role) => {
    const persona = cast[role]!;
    return `- ${role}: ${persona.name}, ${persona.gender}. Always use this exact identity.`;
  });

  let localeRule = "";
  if (resolvedLanguage === "es-ES") {
    localeRule =
      "\n- Use Castilian Spanish appropriate to es-ES. Do not switch the recurring cast to Latin American identities or voices.\n- Carmen and Lucía use feminine self-description forms such as lista, encantada, and preparada. Diego and Álvaro use masculine forms such as listo, encantado, and preparado.";
  } else if (resolvedLanguage === "de-DE") {
    localeRule =
      "\n- Use Standard German appropriate to de-DE.\n- Hanna and Clara use feminine first-person nouns such as Lehrerin, Studentin, and Deutsche. Lukas and Felix use masculine forms such as Lehrer, Student, and Deutscher.";
  } else if (resolvedLanguage === "nl-NL") {
    localeRule =
      "\n- Use Standard Dutch appropriate to nl-NL.\n- Marieke and Lotte use feminine first-person nouns such as lerares, studente, and Nederlandse. Daan and Bram use masculine forms such as leraar, student, and Nederlander.";
  }
  return `Recurring cast (immutable):\n${lines.join("\n")}\n\nPersona rules:\n- A speaker's first-person name must always be their assigned name.\n- First-person adjectives, nationalities, occupations, and participles must agree with that speaker's gender.\n- Never invent or swap a cast member's name. Other names are allowed only when clearly referring to a third person.\n- When the Teacher explains dialogue, attribute examples to the correct named speaker; do not silently speak another persona's first-person identity.${localeRule}`;
}

export function attachPodcastPersona(
  segment: PodcastTranscriptSegment,
  languageCode?: string
): PodcastTranscriptSegment {
  const resolvedLanguage = normalizePersonaLanguageCode(languageCode);
  const persona = getPodcastPersona(resolvedLanguage, segment.speaker);
  if (!persona) return segment;

  return {
    ...segment,
    personaId: persona.id,
    personaName: persona.name,
    personaGender: persona.gender,
    ttsProvider: persona.ttsProvider,
    ttsVoice: persona.ttsVoice,
    ttsLanguage: resolvedLanguage,
    voiceName: persona.ttsVoice,
  };
}

export function getPodcastSpeakerLabel(segment: PodcastTranscriptSegment) {
  return segment.personaName ? `${segment.personaName} · ${segment.speaker}` : segment.speaker;
}
